'use strict';
Object.defineProperty(exports, "__esModule", { value: true });
exports.Panel = void 0;
const $ = require("jquery");
const gsap_1 = require("gsap");
const ScrollTrigger_1 = require("gsap/ScrollTrigger");
const Observer_1 = require("gsap/Observer");
gsap_1.gsap.registerPlugin(ScrollTrigger_1.ScrollTrigger, Observer_1.Observer);
class Panel {
    constructor() {
        // パネル要素のキャッシュ
        this.panels = [];
        // 現在表示中のセクションインデックス
        this.currentIndex = 0;
        // アニメーション中フラグ
        this.isAnimating = false;
        // 初期化完了フラグ（二重初期化・二重破棄防止用）
        this.isInitialized = false;
        // スクロールセクションの操作中に、どのセクションがアクティブかを管理
        this.activeScrollEl = null;
        // パネルセレクターの定義（順序が重要）
        this.PANEL_SELECTORS = [
            '.bulletTopMainVisual',
            '.bulletTopSpecialist',
            '.bulletTopManufacturing',
            '.bulletTopProduct',
            '.bulletTopTechnical',
            '.bulletTopSustainability',
            '.bulletTopRecruit',
        ];
        // 通常スクロールさせるセクション（常時）
        this.SCROLL_SECTIONS = [
            // { selector: '.bulletTopManufacturing', insertBefore: '.bulletTopSustainability' },
            // { selector: '.bulletTopProduct',       insertBefore: '.bulletTopSustainability' },
            // { selector: '.bulletTopTechnical',     insertBefore: '.bulletTopSustainability' },
            { selector: '.bulletTopJournal', insertBefore: '.bulletTopSustainability' },
            // { selector: '.bulletTopRecruit',       insertBefore: '__END__' },
            { selector: '.bulletFooter', insertBefore: '__END__' },
        ];
        // スマホ縦画面時のみ追加されるスクロールセクション
        // ※ insertBefore はスクロールセクション化した要素の「次のパネル」セレクターを指定する
        this.MOBILE_PORTRAIT_SCROLL_SECTIONS = [
            { selector: '.bulletTopManufacturing', insertBefore: '.bulletTopSustainability' },
            { selector: '.bulletTopProduct', insertBefore: '.bulletTopSustainability' },
            { selector: '.bulletTopTechnical', insertBefore: '.bulletTopSustainability' },
        ];
        // ヘッダーセレクター（高さ取得と data-is-scroll 操作用）
        this.HEADER_SELECTOR = '.paramHeader';
        // この高さ（px）以下では通常スクロールにする（スマホ横向き対応）
        this.MIN_HEIGHT = 430;
        // スマホ縦画面の幅閾値（px）
        this.MOBILE_PORTRAIT_MAX_WIDTH = 768;
        // ハッシュ → パネル/スクロールセクション のマッピング定義
        this.HASH_MAP = {
            'mainvisual': { type: 'panel', index: 0 },
            'specialist': { type: 'panel', index: 1 },
            'manufacturing': { type: 'panel', index: 2 },
            'product': { type: 'panel', index: 3 },
            'technical': { type: 'panel', index: 4 },
            'sustainability': { type: 'panel', index: 5 },
            'recruit': { type: 'panel', index: 6 },
            'journal': { type: 'scroll', selector: '.bulletTopJournal' },
            'footer': { type: 'scroll', selector: '.bulletFooter' },
        };
        // Observer インスタンス（スクロール操作の監視）
        this.observer = null;
        // ResizeObserver インスタンス（ヘッダー高さ変化の監視用）
        this.resizeObserver = null;
        // window resize ハンドラの参照（removeEventListener 用）
        this.onWindowResize = null;
        // orientationchange ハンドラの参照（removeEventListener 用）
        this.onOrientationChange = null;
        // 直前のスマホ縦画面状態（リアルタイム切り替えの差分検知用）
        this.prevIsMobilePortrait = false;
        // スクロールセクションのイベントリスナーを管理するマップ
        // removeEventListener で確実に解除するために参照を保持する
        this.scrollListeners = new Map();
        // パネルコンテナが存在しない場合は初期化しない
        if (!document.querySelector('.paramPanelContents'))
            return;
        // window resize と orientationchange を常時監視
        this.setupWindowResizeListener();
        // 初期高さが MIN_HEIGHT より大きい場合のみ初期化
        if (window.innerHeight > this.MIN_HEIGHT) {
            this.prevIsMobilePortrait = this.isMobilePortrait();
            this.init();
        }
    }
    // ─────────────────────────────────────────
    // スマホ縦画面判定
    // ─────────────────────────────────────────
    // portrait かつ幅が MOBILE_PORTRAIT_MAX_WIDTH 以下のときスマホ縦画面とみなす
    isMobilePortrait() {
        const isPortrait = window.matchMedia('(orientation: portrait)').matches;
        return isPortrait && window.innerWidth <= this.MOBILE_PORTRAIT_MAX_WIDTH;
    }
    // ─────────────────────────────────────────
    // 有効なスクロールセクション一覧を返す
    // スマホ縦画面時は MOBILE_PORTRAIT_SCROLL_SECTIONS を先頭に追加して返す
    // ─────────────────────────────────────────
    getEffectiveScrollSections() {
        if (this.isMobilePortrait()) {
            return [
                ...this.MOBILE_PORTRAIT_SCROLL_SECTIONS,
                ...this.SCROLL_SECTIONS,
            ];
        }
        return this.SCROLL_SECTIONS;
    }
    // ─────────────────────────────────────────
    // 初期化
    // ─────────────────────────────────────────
    init() {
        if (this.isInitialized)
            return;
        this.collectPanels();
        this.setupStyles();
        this.setupPanelObserver();
        this.setupScrollSectionListeners();
        this.setupKeyboard();
        this.setupResizeObserver();
        this.setupAnchorLinks();
        this.setupToTopButton();
        this.goToPanel(0, false);
        this.handleInitialHash();
        this.isInitialized = true;
    }
    // ─────────────────────────────────────────
    // クリーンアップ
    // ─────────────────────────────────────────
    // window resize / orientationchange リスナー以外をすべて破棄し、スタイルをリセットする
    destroy() {
        if (!this.isInitialized)
            return;
        // GSAPのObserverを破棄
        this.observer?.kill();
        this.observer = null;
        // ResizeObserver を破棄（init で再生成するため）
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        // ScrollTrigger インスタンスをすべて破棄
        ScrollTrigger_1.ScrollTrigger.getAll().forEach(t => t.kill());
        // スタイルをリセット（有効なスクロールセクション全体を対象にする）
        gsap_1.gsap.set('body', { overflow: '' });
        this.panels.forEach(panel => gsap_1.gsap.set(panel, { clearProps: 'all' }));
        // destroy 時点の有効なスクロールセクションをすべてリセット
        // MOBILE_PORTRAIT_SCROLL_SECTIONS も含めてリセットしないとスタイルが残る
        const allScrollSections = [
            ...this.SCROLL_SECTIONS,
            ...this.MOBILE_PORTRAIT_SCROLL_SECTIONS,
        ];
        allScrollSections.forEach(({ selector }) => {
            const el = document.querySelector(selector);
            if (el)
                gsap_1.gsap.set(el, { clearProps: 'all' });
        });
        // キーボードイベントを解除
        $(document).off('keydown');
        $(document).off('click.panelAnchor');
        // 内部状態をリセット
        this.panels = [];
        this.currentIndex = 0;
        this.isAnimating = false;
        this.activeScrollEl = null;
        this.isInitialized = false;
    }
    // ─────────────────────────────────────────
    // window resize / orientationchange（常時稼働）
    // ─────────────────────────────────────────
    setupWindowResizeListener() {
        // ① 高さの変化（スマホ横向き → MIN_HEIGHT 判定）
        this.onWindowResize = () => {
            if (window.innerHeight <= this.MIN_HEIGHT) {
                if (this.isInitialized)
                    this.destroy();
            }
            else {
                if (!this.isInitialized) {
                    this.prevIsMobilePortrait = this.isMobilePortrait();
                    this.init();
                    return;
                }
                // ② portrait ↔ landscape の切り替えをリアルタイムで検知
                this.handleOrientationSwitch();
            }
        };
        window.addEventListener('resize', this.onWindowResize);
        // ③ orientationchange イベントでも同様に検知（resize より早く発火するブラウザ対策）
        this.onOrientationChange = () => {
            if (!this.isInitialized)
                return;
            // orientationchange 直後は innerWidth/Height が更新されていない場合があるため
            // 短い遅延を挟んで確実な値を取得する
            setTimeout(() => {
                if (window.innerHeight <= this.MIN_HEIGHT) {
                    this.destroy();
                    return;
                }
                this.handleOrientationSwitch();
            }, 100);
        };
        window.addEventListener('orientationchange', this.onOrientationChange);
    }
    // portrait ↔ landscape の切り替え時にリアルタイムでスタイルを再適用する
    handleOrientationSwitch() {
        const current = this.isMobilePortrait();
        if (current === this.prevIsMobilePortrait)
            return; // 変化なし → 何もしない
        this.prevIsMobilePortrait = current;
        // スタイルと Observer を一度リセットしてから再適用
        this.refreshScrollSections();
    }
    // スクロールセクションのスタイルを現在の状態に合わせて再構築する
    // パネルスタイルも再適用して portrait ↔ landscape 切り替え時の overflow 残留を防ぐ
    refreshScrollSections() {
        const headerHeight = this.getHeaderHeight();
        // まず全スクロールセクション（mobile 用を含む）をリセット
        const allScrollSections = [
            ...this.SCROLL_SECTIONS,
            ...this.MOBILE_PORTRAIT_SCROLL_SECTIONS,
        ];
        allScrollSections.forEach(({ selector }) => {
            const el = document.querySelector(selector);
            if (el)
                gsap_1.gsap.set(el, { clearProps: 'all' });
        });
        // スマホ縦画面でスクロールセクション化される要素のセレクター一覧
        const mobileScrollSelectors = this.MOBILE_PORTRAIT_SCROLL_SECTIONS.map(s => s.selector);
        // パネルスタイルを再適用
        // スクロールセクション化対象のパネルは clearProps してスクロールセクション側に委ねる
        this.panels.forEach((panel, i) => {
            const selector = this.PANEL_SELECTORS[i];
            if (this.isMobilePortrait() && mobileScrollSelectors.includes(selector)) {
                gsap_1.gsap.set(panel, { clearProps: 'all' });
                return;
            }
            gsap_1.gsap.set(panel, {
                position: 'fixed',
                top: headerHeight,
                left: 0,
                width: '100%',
                height: `calc(100% - ${headerHeight}px)`,
                zIndex: this.panels.length - i,
                yPercent: 100, // goToPanel(0) で上書きされるため一旦全部画面外へ
                overflow: 'hidden',
            });
        });
        // 現在有効なスクロールセクションにスタイルを再適用
        this.getEffectiveScrollSections().forEach(({ selector }) => {
            const el = document.querySelector(selector);
            if (el) {
                gsap_1.gsap.set(el, {
                    position: 'fixed',
                    top: headerHeight,
                    left: 0,
                    width: '100%',
                    height: `calc(100% - ${headerHeight}px)`,
                    zIndex: 0,
                    yPercent: 100,
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                });
            }
        });
        // currentIndex をリセットして最初のパネルへ戻す
        // （セクション構成が変わるため、現在位置の整合性を保証できないため）
        this.currentIndex = 0;
        this.activeScrollEl = null;
        this.observer?.enable();
        this.goToPanel(0, false);
        this.unsetScrolled();
        // スクロールセクションのイベントリスナーを再登録
        this.setupScrollSectionListeners();
    }
    // ─────────────────────────────────────────
    // ResizeObserver（ヘッダー高さ変化を監視）
    // ─────────────────────────────────────────
    setupResizeObserver() {
        const header = document.querySelector(this.HEADER_SELECTOR);
        if (!header)
            return;
        this.resizeObserver = new ResizeObserver(() => {
            this.applyLayoutStyles();
        });
        this.resizeObserver.observe(header);
    }
    setupToTopButton() {
        // ページトップボタンが存在する場合のみイベントをセットアップ
        if (document.querySelector('.trigToTop')) {
            $('.trigToTop').click(() => {
                this.setScrolled();
                this.goToPanel(0);
                return false;
            });
        }
    }
    // ─────────────────────────────────────────
    // ヘッダー・スクロール状態
    // ─────────────────────────────────────────
    setScrolled() {
        const header = document.querySelector(this.HEADER_SELECTOR);
        if (header && header.dataset.isScroll !== 'true') {
            header.dataset.isScroll = 'true';
        }
    }
    unsetScrolled() {
        const header = document.querySelector(this.HEADER_SELECTOR);
        if (header && header.dataset.isScroll !== 'false') {
            header.dataset.isScroll = 'false';
        }
    }
    getHeaderHeight() {
        const header = document.querySelector(this.HEADER_SELECTOR);
        return header ? header.offsetHeight : 0;
    }
    // ─────────────────────────────────────────
    // セットアップ
    // ─────────────────────────────────────────
    collectPanels() {
        this.panels = this.PANEL_SELECTORS
            .map(sel => document.querySelector(sel))
            .filter((el) => el !== null);
    }
    // ヘッダーの高さに合わせてパネルとスクロールセクションの位置と高さを調整
    applyLayoutStyles() {
        const headerHeight = this.getHeaderHeight();
        this.panels.forEach((panel) => {
            gsap_1.gsap.set(panel, {
                top: headerHeight,
                height: `calc(100% - ${headerHeight}px)`,
            });
        });
        // 有効なスクロールセクションのみ対象にする
        this.getEffectiveScrollSections().forEach(({ selector }) => {
            const el = document.querySelector(selector);
            if (el) {
                gsap_1.gsap.set(el, {
                    top: headerHeight,
                    height: `calc(100% - ${headerHeight}px)`,
                });
            }
        });
    }
    // 初期スタイルの設定
    setupStyles() {
        gsap_1.gsap.set('body', { overflow: 'hidden' });
        const headerHeight = this.getHeaderHeight();
        // スマホ縦画面でスクロールセクション化される要素のセレクター一覧
        const mobileScrollSelectors = this.MOBILE_PORTRAIT_SCROLL_SECTIONS.map(s => s.selector);
        this.panels.forEach((panel, i) => {
            const selector = this.PANEL_SELECTORS[i];
            // スマホ縦画面かつスクロールセクション化対象のパネルはここでは設定しない
            // （後続の getEffectiveScrollSections() ループでスクロール用スタイルが適用される）
            if (this.isMobilePortrait() && mobileScrollSelectors.includes(selector)) {
                return;
            }
            gsap_1.gsap.set(panel, {
                position: 'fixed',
                top: headerHeight,
                left: 0,
                width: '100%',
                height: `calc(100% - ${headerHeight}px)`,
                zIndex: this.panels.length - i,
                yPercent: i === 0 ? 0 : 100,
                overflow: 'hidden',
            });
        });
        // 有効なスクロールセクションのみ対象にする
        this.getEffectiveScrollSections().forEach(({ selector }) => {
            const el = document.querySelector(selector);
            if (el) {
                gsap_1.gsap.set(el, {
                    position: 'fixed',
                    top: headerHeight,
                    left: 0,
                    width: '100%',
                    height: `calc(100% - ${headerHeight}px)`,
                    zIndex: 0,
                    yPercent: 100,
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                });
            }
        });
    }
    // パネル全体のスクロール操作を監視する Observer をセットアップ
    setupPanelObserver() {
        this.observer = Observer_1.Observer.create({
            type: 'wheel,touch',
            wheelSpeed: -1,
            onDown: () => {
                this.setScrolled();
                this.handleScroll('prev');
            },
            onUp: () => {
                this.setScrolled();
                this.handleScroll('next');
            },
            tolerance: 10,
            preventDefault: true,
        });
    }
    // 各スクロールセクションに対して、端に到達したときのイベントリスナーをセットアップ
    // 呼び出し前に既存リスナーを解除するため、重複登録が発生しない
    setupScrollSectionListeners() {
        // 既存リスナーをすべて解除してからマップをクリア
        this.scrollListeners.forEach((handlers, el) => {
            el.removeEventListener('wheel', handlers.wheel);
            el.removeEventListener('touchstart', handlers.touchstart);
            el.removeEventListener('touchend', handlers.touchend);
        });
        this.scrollListeners.clear();
        this.getEffectiveScrollSections().forEach(({ selector }) => {
            const el = document.querySelector(selector);
            if (!el)
                return;
            let touchStartY = 0;
            const onWheel = (e) => {
                if (this.activeScrollEl !== el || this.isAnimating)
                    return;
                this.setScrolled();
                const atTop = el.scrollTop <= 0;
                const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
                if (e.deltaY < 0 && atTop) {
                    e.preventDefault();
                    this.navigateFromScrollSection('prev');
                }
                else if (e.deltaY > 0 && atBottom) {
                    e.preventDefault();
                    this.navigateFromScrollSection('next');
                }
            };
            const onTouchStart = (e) => {
                touchStartY = e.touches[0].clientY;
            };
            const onTouchEnd = (e) => {
                if (this.activeScrollEl !== el || this.isAnimating)
                    return;
                this.setScrolled();
                const delta = touchStartY - e.changedTouches[0].clientY;
                const atTop = el.scrollTop <= 0;
                const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
                if (delta < -40 && atTop)
                    this.navigateFromScrollSection('prev');
                if (delta > 40 && atBottom)
                    this.navigateFromScrollSection('next');
            };
            el.addEventListener('wheel', onWheel, { passive: false });
            el.addEventListener('touchstart', onTouchStart, { passive: true });
            el.addEventListener('touchend', onTouchEnd, { passive: true });
            // 解除用に参照を保持
            this.scrollListeners.set(el, {
                wheel: onWheel,
                touchstart: onTouchStart,
                touchend: onTouchEnd,
            });
        });
    }
    // キーボードの矢印キーとページアップ/ダウンキーでのスクロール操作をセットアップ
    setupKeyboard() {
        $(document).on('keydown', (e) => {
            if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                if (this.activeScrollEl) {
                    const atBottom = this.activeScrollEl.scrollTop + this.activeScrollEl.clientHeight
                        >= this.activeScrollEl.scrollHeight - 1;
                    if (!atBottom)
                        return;
                }
                this.setScrolled();
                this.handleScroll('next');
            }
            if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                if (this.activeScrollEl) {
                    const atTop = this.activeScrollEl.scrollTop <= 0;
                    if (!atTop)
                        return;
                }
                this.setScrolled();
                this.handleScroll('prev');
            }
        });
    }
    setupAnchorLinks() {
        $(document).on('click.panelAnchor', 'a[href^="#"]', (e) => {
            const href = $(e.currentTarget).attr('href');
            if (!href || href === '#')
                return;
            const hash = href.slice(1); // '#' を除去
            // HASH_MAP に定義されているか確認
            const target = this.HASH_MAP[hash];
            if (!target)
                return;
            // デフォルトのジャンプを抑制
            e.preventDefault();
            if (target.type === 'panel') {
                this.setScrolled();
                this.goToPanel(target.index, true);
                if (target.index === 0)
                    this.unsetScrolled();
                return;
            }
            if (target.type === 'scroll') {
                const el = document.querySelector(target.selector);
                if (!el)
                    return;
                const allSections = this.getAllSections();
                const sectionIndex = allSections.findIndex(s => s.type === 'scroll' && s.selector === target.selector);
                if (sectionIndex === -1)
                    return;
                this.setScrolled();
                const direction = sectionIndex > this.currentIndex ? 'next' : 'prev';
                this.goToSection(sectionIndex, direction);
            }
        });
    }
    // ─────────────────────────────────────────
    // ナビゲーション
    // ─────────────────────────────────────────
    handleScroll(direction) {
        if (this.isAnimating)
            return;
        const allSections = this.getAllSections();
        const nextIndex = direction === 'next'
            ? this.currentIndex + 1
            : this.currentIndex - 1;
        if (nextIndex < 0 || nextIndex >= allSections.length)
            return;
        this.goToSection(nextIndex, direction);
    }
    navigateFromScrollSection(direction) {
        if (this.isAnimating)
            return;
        const allSections = this.getAllSections();
        const nextIndex = direction === 'next'
            ? this.currentIndex + 1
            : this.currentIndex - 1;
        if (nextIndex < 0 || nextIndex >= allSections.length)
            return;
        this.goToSection(nextIndex, direction);
    }
    // パネルとスクロールセクションを設定された順序でまとめて取得する
    // ※ getEffectiveScrollSections() を参照するため、スマホ縦画面時は自動的に
    //   MOBILE_PORTRAIT_SCROLL_SECTIONS が含まれた順序になる
    getAllSections() {
        const sections = [];
        const insertMap = new Map();
        this.getEffectiveScrollSections().forEach(({ selector, insertBefore }) => {
            const el = document.querySelector(selector);
            if (el && insertBefore !== '__END__') {
                if (!insertMap.has(insertBefore))
                    insertMap.set(insertBefore, []);
                insertMap.get(insertBefore).push({ type: 'scroll', el, selector });
            }
        });
        // スクロールセクション化されているセレクターのセット（スマホ縦画面時に使用）
        const scrollSectionSelectors = new Set(this.getEffectiveScrollSections().map(s => s.selector));
        this.PANEL_SELECTORS.forEach((sel, i) => {
            // スクロールセクション側（insertMap）にすでに追加済みのセレクターは
            // パネルとして重複登録しない
            if (scrollSectionSelectors.has(sel))
                return;
            const scrollSecs = insertMap.get(sel);
            if (scrollSecs) {
                scrollSecs.forEach(s => sections.push(s));
            }
            sections.push({ type: 'panel', index: i });
        });
        this.getEffectiveScrollSections().forEach(({ selector, insertBefore }) => {
            if (insertBefore === '__END__') {
                const el = document.querySelector(selector);
                if (el)
                    sections.push({ type: 'scroll', el, selector });
            }
        });
        return sections;
    }
    goToSection(targetSectionIndex, direction) {
        this.isAnimating = true;
        const allSections = this.getAllSections();
        const target = allSections[targetSectionIndex];
        const current = allSections[this.currentIndex];
        const duration = 0.9;
        const ease = 'power2.inOut';
        const enterY = direction === 'next' ? 100 : -100;
        const leaveY = direction === 'next' ? -100 : 100;
        if (target.type === 'scroll') {
            this.observer?.disable();
            gsap_1.gsap.fromTo(target.el, { yPercent: enterY }, {
                yPercent: 0,
                duration,
                ease,
                onStart: () => {
                    target.el.scrollTop = direction === 'next' ? 0 : target.el.scrollHeight;
                },
                onComplete: () => {
                    this.isAnimating = false;
                    this.activeScrollEl = target.el;
                    this.currentIndex = targetSectionIndex;
                },
            });
        }
        else {
            const targetPanel = this.panels[target.index];
            gsap_1.gsap.fromTo(targetPanel, { yPercent: enterY }, {
                yPercent: 0,
                duration,
                ease,
                onComplete: () => {
                    this.isAnimating = false;
                    this.activeScrollEl = null;
                    this.currentIndex = targetSectionIndex;
                    this.observer?.enable();
                    if (target.index === 0)
                        this.unsetScrolled();
                },
            });
        }
        if (current.type === 'panel') {
            gsap_1.gsap.to(this.panels[current.index], { yPercent: leaveY, duration, ease });
        }
        else if (current.type === 'scroll') {
            gsap_1.gsap.to(current.el, { yPercent: leaveY, duration, ease });
        }
    }
    goToPanel(panelIndex, animate = true) {
        const allSections = this.getAllSections();
        const sectionIndex = allSections.findIndex(s => s.type === 'panel' && s.index === panelIndex);
        if (sectionIndex === -1)
            return;
        if (!animate) {
            this.panels.forEach((panel, i) => {
                gsap_1.gsap.set(panel, { yPercent: i === panelIndex ? 0 : 100 });
            });
            // 有効なスクロールセクションのみ画面外へ
            this.getEffectiveScrollSections().forEach(({ selector }) => {
                const el = document.querySelector(selector);
                if (el)
                    gsap_1.gsap.set(el, { yPercent: 100 });
            });
            this.currentIndex = sectionIndex;
            this.activeScrollEl = null;
        }
        else {
            const direction = sectionIndex > this.currentIndex ? 'next' : 'prev';
            this.goToSection(sectionIndex, direction);
        }
    }
    handleInitialHash() {
        const hash = window.location.hash.slice(1);
        if (!hash)
            return;
        const panelMatch = hash.match(/^panel-(\d+)$/);
        if (panelMatch) {
            const index = parseInt(panelMatch[1], 10);
            if (index >= 0 && index < this.panels.length) {
                this.goToPanel(index, false);
                if (index > 0)
                    this.setScrolled();
            }
            return;
        }
        const target = this.HASH_MAP[hash];
        if (!target)
            return;
        if (target.type === 'panel') {
            this.goToPanel(target.index, false);
            if (target.index > 0)
                this.setScrolled();
            return;
        }
        if (target.type === 'scroll') {
            const el = document.querySelector(target.selector);
            if (!el)
                return;
            const allSections = this.getAllSections();
            const sectionIndex = allSections.findIndex(s => s.type === 'scroll' && s.selector === target.selector);
            if (sectionIndex === -1)
                return;
            this.panels.forEach(p => gsap_1.gsap.set(p, { yPercent: 100 }));
            // 有効なスクロールセクションのみ画面外へ
            this.getEffectiveScrollSections().forEach(({ selector }) => {
                const sec = document.querySelector(selector);
                if (sec)
                    gsap_1.gsap.set(sec, { yPercent: 100 });
            });
            gsap_1.gsap.set(el, { yPercent: 0 });
            el.scrollTop = 0;
            this.currentIndex = sectionIndex;
            this.activeScrollEl = el;
            this.observer?.disable();
            this.setScrolled();
        }
    }
}
exports.Panel = Panel;
exports.default = Panel;
